import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, Loader2, ArrowLeft, Sun, Moon, Eye, EyeOff, CheckCircle2, AlertCircle } from '@/components/icons';
import { resetPassword } from '../../lib/auth-client';
import { useTheme } from '../../App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import StackSketchLogo from '/StackSketch-FullLogo.svg';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDark, toggleTheme } = useTheme();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Check if token exists
  useEffect(() => {
    if (!token) {
      setTokenError('Invalid reset link. Please request a new password reset.');
    }
  }, [token]);

  const getPasswordStrength = (pwd: string): { strength: 'weak' | 'medium' | 'strong'; label: string } => {
    if (pwd.length === 0) return { strength: 'weak', label: '' };
    if (pwd.length < 8) return { strength: 'weak', label: 'Too short' };
    
    const hasUpper = /[A-Z]/.test(pwd);
    const hasLower = /[a-z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSpecial = /[^A-Za-z0-9]/.test(pwd);
    
    const score = [hasUpper, hasLower, hasNumber, hasSpecial].filter(Boolean).length;
    
    if (score <= 2) return { strength: 'weak', label: 'Weak' };
    if (score === 3) return { strength: 'medium', label: 'Medium' };
    return { strength: 'strong', label: 'Strong' };
  };

  const passwordStrength = getPasswordStrength(password);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Invalid reset link');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setIsLoading(true);

    try {
      const result = await resetPassword(token, password);
      if (result.error) {
        setError(result.error.message || 'Failed to reset password');
      } else {
        setSuccess(true);
        // Redirect to sign in after 3 seconds
        setTimeout(() => {
          navigate('/auth');
        }, 3000);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (tokenError) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-background [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]">
        <div className="mb-8 text-center">
          <img src={StackSketchLogo} alt="Stack Sketch" className="w-32 h-auto mx-auto mb-4 dark:brightness-0 dark:invert" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md"
        >
          <Card className="border-red-200 dark:border-red-800 shadow-xl">
            <CardContent className="p-6">
              <div className="flex items-start gap-3 mb-4">
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-bold text-foreground dark:text-foreground mb-2">
                    Invalid Reset Link
                  </h3>
                  <p className="text-sm text-muted-foreground dark:text-muted-foreground mb-4">
                    {tokenError}
                  </p>
                  <Link
                    to="/forgot-password"
                    className="text-primary hover:text-primary/80 font-medium text-sm"
                  >
                    Request a new password reset →
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <button
          onClick={toggleTheme}
          className="fixed bottom-4 right-4 p-3 bg-card dark:bg-card border border-border dark:border-border rounded-full shadow-lg hover:bg-muted dark:hover:bg-muted transition-colors"
        >
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 bg-background [background-image:radial-gradient(var(--border)_1px,transparent_1px)] [background-size:24px_24px]">
      {/* Logo and branding */}
      <div className="mb-8 text-center">
        <img src={StackSketchLogo} alt="Stack Sketch" className="w-32 h-auto mx-auto mb-4 dark:brightness-0 dark:invert" />
        <p className="text-muted-foreground dark:text-muted-foreground">
          Reset your password
        </p>
      </div>

      {/* Auth Card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="w-full max-w-md"
      >
        <Card className="border-border dark:border-border shadow-xl">
          {/* Header */}
          <CardHeader className="border-b border-border dark:border-border">
            <h3 className="text-lg font-bold text-foreground dark:text-foreground">
              Set New Password
            </h3>
            <CardDescription className="text-xs">
              Enter your new password below
            </CardDescription>
          </CardHeader>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <CardContent className="p-6 space-y-4">
              <AnimatePresence mode="wait">
                {error && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Alert variant="destructive">
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  </motion.div>
                )}

                {success && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                  >
                    <Alert>
                      <AlertDescription>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="font-semibold mb-1">Password reset successful!</p>
                            <p>Redirecting to sign in page...</p>
                          </div>
                        </div>
                      </AlertDescription>
                    </Alert>
                  </motion.div>
                )}
              </AnimatePresence>

              {!success && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={8}
                        disabled={isLoading}
                        className="pl-10 pr-12"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    {password && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex-1 h-2 bg-muted dark:bg-muted rounded-full overflow-hidden">
                            <div
                              className={`h-full transition-all ${
                                passwordStrength.strength === 'weak'
                                  ? 'bg-red-500 w-1/3'
                                  : passwordStrength.strength === 'medium'
                                  ? 'bg-yellow-500 w-2/3'
                                  : 'bg-green-500 w-full'
                              }`}
                            />
                          </div>
                          <span
                            className={`text-xs font-medium ${
                              passwordStrength.strength === 'weak'
                                ? 'text-red-600 dark:text-red-400'
                                : passwordStrength.strength === 'medium'
                                ? 'text-yellow-600 dark:text-yellow-400'
                                : 'text-green-600 dark:text-green-400'
                            }`}
                          >
                            {passwordStrength.label}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground dark:text-muted-foreground">
                          Use at least 8 characters with a mix of letters, numbers, and symbols
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="••••••••"
                        required
                        minLength={8}
                        disabled={isLoading}
                        className="pl-10 pr-12"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0 text-muted-foreground hover:text-muted-foreground dark:hover:text-muted-foreground"
                      >
                        {showConfirmPassword ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                    {confirmPassword && password !== confirmPassword && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Passwords do not match
                      </p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading || password !== confirmPassword || password.length < 8}
                    className="w-full"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Resetting password...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </form>

          {/* Footer */}
          {!success && (
            <CardFooter className="border-t border-border dark:border-border">
              <Link
                to="/auth"
                className="text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1 mx-auto"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </Link>
            </CardFooter>
          )}
        </Card>
      </motion.div>

      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="fixed bottom-4 right-4 p-3 bg-card dark:bg-card border border-border dark:border-border rounded-full shadow-lg hover:bg-muted dark:hover:bg-muted transition-colors"
      >
        {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
      </button>
    </div>
  );
}